import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import Layout from './components/Layout'
import ClientActivityTracker from './components/ClientActivityTracker'
import Login from './pages/Login'
import SetPassword from './pages/SetPassword'
import ClientAccount from './pages/client/Account'
import ClientAccountSettings from './pages/client/AccountSettings'
import ClientRateEstimator from './pages/client/RateEstimator'
import AdminDashboard from './pages/admin/Dashboard'
import AdminDataManagement from './pages/admin/DataManagement'
import AdminClients from './pages/admin/Clients'
import AdminClientDetail from './pages/admin/ClientDetail'
import AdminArchivedClients from './pages/admin/ArchivedClients'
import AdminUnmatchedExports from './pages/admin/UnmatchedExports'
import AdminUnmatchedEasyship from './pages/admin/UnmatchedEasyship'
import AdminUnassignedOrders from './pages/admin/UnassignedOrders'
import AdminAllActivities from './pages/admin/AllActivities'
import AdminBilling from './pages/admin/Billing'
import AdminHistory from './pages/admin/History'
import AdminMtdLedger from './pages/admin/MtdLedger'
import AdminFedexReconciliation from './pages/admin/FedexReconciliation'
import AdminUpsReconciliation from './pages/admin/UpsReconciliation'
import AdminAuspostReconciliation from './pages/admin/AuspostReconciliation'
import AdminAuspostInvoices from './pages/admin/AuspostInvoices'
import AdminAuspostInvoiceDetail from './pages/admin/AuspostInvoiceDetail'
import AdminClientUsers from './pages/admin/ClientUsers'
import AdminTracking from './pages/admin/Tracking'
import AdminRateEstimator from './pages/admin/RateEstimator'

function ProtectedRoute({ children, requiredRole }) {
  const { user, role, loading, forcePasswordChange } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // First-time invitee: route them to the password-setup screen before
  // anything else loads (no dashboard glimpse, no API calls, no role
  // mismatch dance — the flag wins).
  if (forcePasswordChange) {
    return <Navigate to="/set-password" replace />
  }

  if (requiredRole && role !== requiredRole) {
    if (role === 'admin') return <Navigate to="/admin" replace />
    return <Navigate to="/account" replace />
  }

  return children
}

function RootRedirect() {
  const { user, role, loading, forcePasswordChange } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) return <Navigate to="/login" replace />
  if (forcePasswordChange) return <Navigate to="/set-password" replace />
  if (role === 'admin') return <Navigate to="/admin" replace />
  return <Navigate to="/account" replace />
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />

        {/* First-time password setup. Doesn't use ProtectedRoute
            because the route guard there always bounces forced-change
            users HERE — wrapping it in the guard would create a
            redirect loop. The page itself handles the no-session case
            by punting to /login. */}
        <Route path="/set-password" element={<SetPassword />} />

        {/* Root redirect */}
        <Route path="/" element={<RootRedirect />} />

        {/* Client routes — the entire portal lives behind /account.
            URL shapes:
              /account                       → resolves user → first client → overview
              /account/:clientId             → defaults to overview
              /account/:clientId/:tab        → specific tab (orders, storage, …)

            Nesting order:
              ProtectedRoute("client")   — auth guard
                ClientActivityTracker    — records nav events (audit log)
                  Layout                 — chrome (sidebar, header, main)
                    <page>               — actual content
            Tracker sits BETWEEN the guard and Layout so admin nav —
            which uses the same Layout under a different guard — never
            fires activity events. */}
        <Route
          path="/"
          element={
            <ProtectedRoute requiredRole="client">
              <ClientActivityTracker />
            </ProtectedRoute>
          }
        >
          <Route element={<Layout />}>
            <Route path="account"                     element={<ClientAccount />} />
            <Route path="account/:clientId"           element={<ClientAccount />} />
            <Route path="account/:clientId/:tab"      element={<ClientAccount />} />
            <Route path="account-settings"            element={<ClientAccountSettings />} />
            <Route path="rate-estimator"              element={<ClientRateEstimator />} />
          </Route>
        </Route>

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="data-management" element={<AdminDataManagement />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="clients/archived" element={<AdminArchivedClients />} />
          <Route path="clients/:id" element={<AdminClientDetail />} />
          <Route path="unmatched-exports" element={<AdminUnmatchedExports />} />
          <Route path="unmatched-easyship" element={<AdminUnmatchedEasyship />} />
          <Route path="unassigned-orders" element={<AdminUnassignedOrders />} />
          <Route path="activities" element={<AdminAllActivities />} />
          <Route path="billing" element={<AdminBilling />} />
          <Route path="history" element={<AdminHistory />} />
          <Route path="mtd-log" element={<AdminMtdLedger />} />
          <Route path="fedex" element={<AdminFedexReconciliation />} />
          <Route path="ups" element={<AdminUpsReconciliation />} />
          <Route path="auspost" element={<AdminAuspostReconciliation />} />
          <Route path="auspost/invoices" element={<AdminAuspostInvoices />} />
          <Route path="auspost/invoices/:invoiceId" element={<AdminAuspostInvoiceDetail />} />
          <Route path="client-users" element={<AdminClientUsers />} />
          {/* Tracking dashboard — index falls through to Overview; tabs read
              from URL so users can deep-link and share filter views. */}
          <Route path="tracking" element={<AdminTracking />} />
          <Route path="tracking/:tab" element={<AdminTracking />} />
          {/* Rate Estimator — dual-route like Tracking so the single/bulk
              sub-tabs deep-link and share. */}
          <Route path="rate-estimator" element={<AdminRateEstimator />} />
          <Route path="rate-estimator/:tab" element={<AdminRateEstimator />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
